import { ArrowRight, MoreHorizontal } from "lucide-react";
import React, { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  createStyles,
  Group,
  Paper,
  Progress,
  Space,
  Text,
} from "@mantine/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import SurveyQuestion from "../services/model/SurveyQuestion";
import ViewAllResponsesModal from "./ViewAllResponsesModal";

dayjs.extend(relativeTime);

type SurveyQuestionCardProps = {
  questionNumber: number;
  question: SurveyQuestion;
  total: number;
};

const useStyles = createStyles(() => ({
  textAnswer: {
    alignSelf: "stretch",
    flexGrow: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "5px",
    padding: "14px 16px",
    borderRadius: "4px",
    backgroundColor: "#f7f7f8",
  },
  oddTextAnswer: {
    backgroundColor: "#ffffff",
  },
}));

const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) {
    // we don't want to divide by zero here, will result in NaN
    return 0;
  }
  return parseFloat(((100 * value) / total).toFixed(2));
};

const SurveyQuestionCard: React.FC<SurveyQuestionCardProps> = ({
  question,
  questionNumber,
  total,
}) => {
  const { classes, cx } = useStyles();
  const [viewAllModalOpened, setViewAllModalOpened] = useState(false);

  return (
    <Paper
      shadow="sm"
      radius="md"
      p="xl"
      withBorder
      key={
        //  eslint-disable-next-line react/no-array-index-key
        `questions-${question.id}`
      }
    >
      <div className="question-card-header">
        <div className="question-card-chip-wrapper">
          <Badge className="question-number-indicator">
            {questionNumber}/{total}
          </Badge>
          <Text className="question-type-label">
            {Object.is(question.question_type, "free-text")
              ? "Open-ended"
              : "Multiple choice"}
          </Text>
        </div>
        <ActionIcon variant="transparent" color="dark" size="lg" radius={8}>
          <MoreHorizontal size={16} />
        </ActionIcon>
      </div>
      <Space h="md" />
      <div>
        <Text>{question.content}</Text>
        <Space h="xl" />
        {!Object.is(question.question_type, "mcq") &&
          question.responses.length > 1 && (
            <Group
              style={{
                alignSelf: "stretch",
                flexGrow: 0,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "16px 4px 12px",
              }}
            >
              <Text
                style={{
                  flexGrow: 0,
                  fontSize: "12px",
                  fontWeight: 500,
                  fontStretch: "normal",
                  fontStyle: "normal",
                  lineHeight: 1.33,
                  letterSpacing: "0.16px",
                  textAlign: "left",
                  alignSelf: "center",
                  color: "495057",
                }}
              >
                Top Responses
              </Text>

              <Button
                variant="subtle"
                color="jem-theme"
                rightIcon={<ArrowRight />}
                onClick={() => setViewAllModalOpened(true)}
              >
                View all
              </Button>
            </Group>
          )}
        <Space h="xl" />
        {Object.is(question.question_type, "mcq")
          ? question.choices.map((it) => (
              <div key={`question-choices-${it.id}`}>
                <Group position="apart" mt="xs">
                  <Group spacing="xl">
                    <Text size="sm" color="dimmed">
                      {it.content}
                    </Text>
                    <Text size="sm" color="dimmed">
                      {`${
                        question.responses.filter(
                          (response) =>
                            response.form_question_choice?.id === it.id
                        ).length
                      } responses`}
                    </Text>
                  </Group>

                  <Text size="sm" color="dimmed">
                    {`${calculatePercentage(
                      question.responses.filter(
                        (response) =>
                          response.form_question_choice?.id === it.id
                      ).length,
                      question.responses.length
                    )}%`}
                  </Text>
                </Group>

                <Progress
                  size={20}
                  value={calculatePercentage(
                    question.responses.filter(
                      (response) => response.form_question_choice?.id === it.id
                    ).length,
                    question.responses.length
                  )}
                  mt={5}
                />
              </div>
            ))
          : question.responses
              .filter((it, index) => index < 3)
              .map((it, index) => (
                <div
                  key={it.id}
                  className={cx(classes.textAnswer, {
                    [classes.oddTextAnswer]: index % 2,
                  })}
                >
                  <Text size="sm">{it.text_answer}</Text>
                  <Space h="sm" />
                  <Group spacing="xl">
                    <Text size="sm">{`${it.employee.first_name} ${it.employee.last_name}`}</Text>
                    <Text size="xs" color="dimmed">
                      {dayjs(it.date_created).fromNow()}
                    </Text>
                  </Group>
                </div>
              ))}
      </div>

      <ViewAllResponsesModal
        opened={viewAllModalOpened}
        onClose={() => setViewAllModalOpened(false)}
        question={question}
      />
    </Paper>
  );
};

export default SurveyQuestionCard;
